import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BrushIcon from '@mui/icons-material/Brush';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_INPAINT_STATE,
  buildInpaintRequest,
  hasSourceImage,
  type InpaintFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { mergePromptWithSwatches } from '../../shared/panels/swatchSelection';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  maskDataUrl: string | null;
  swatchPrompt?: string;
  onResult?: (result: OperationResult) => void;
  onRequestClearMask?: () => void;
}

export const InpaintPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  maskDataUrl,
  swatchPrompt,
  onResult,
  onRequestClearMask,
}) => {
  const [state, setState] = useState<InpaintFormState>(DEFAULT_INPAINT_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.inpaint);

  const hasSource = hasSourceImage(source);
  const hasMask = !!maskDataUrl;
  const promptOk = state.prompt.trim().length > 0;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required for inpaint.');
      return;
    }
    try {
      const sid = await ensureSession();
      const mergedPrompt = mergePromptWithSwatches(state.prompt, swatchPrompt ?? '');
      const request = buildInpaintRequest(
        { ...state, prompt: mergedPrompt },
        sid,
        source,
        maskDataUrl,
      );
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !hasMask || !promptOk;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <BrushIcon fontSize="small" />
        <Typography variant="subtitle2">Inpaint</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to inpaint.</Alert>
      )}
      {hasSource && !hasMask && (
        <Alert severity="info">Paint a mask on the canvas to mark the region to fill.</Alert>
      )}

      <TextField
        label="Prompt"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        placeholder="Describe what to paint into the masked region"
        multiline
        minRows={2}
        maxRows={5}
        size="small"
        fullWidth
        required
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Strength · {state.strength.toFixed(2)}
        </Typography>
        <Slider
          value={state.strength}
          onChange={(_, v) => setState(s => ({ ...s, strength: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Inpaint failed.'}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <BrushIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Inpainting…' : 'Inpaint'}
        </Button>
        {onRequestClearMask && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRequestClearMask}
            disabled={!hasMask || mutation.isPending}
          >
            Clear mask
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
